import React, { useRef, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Picker from '@emoji-mart/react'
import data from '@emoji-mart/data'
import { Box, Text, ActionIcon } from '@mantine/core'
import { useField } from 'formik'
import { cn } from '@/shared/lib/utils'
import { Smile } from 'lucide-react'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'

interface Props {
  name: string
  label?: string
  className?: string
}

export const QuillEditorField: React.FC<Props> = ({ name, label, className }) => {
  const [field, meta, helpers] = useField(name)
  const [isFocused, setIsFocused] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const { language } = useLanguageStore()

  const quillRef = useRef<ReactQuill>(null)

  const handleEmojiSelect = (emoji: any) => {
    const editor = quillRef.current?.getEditor()
    const range = editor?.getSelection()
    if (editor && range && emoji?.native) {
      editor.insertText(range.index, emoji.native)
      editor.setSelection(range.index + emoji.native.length)
    }

    setShowEmojiPicker(false)
  }

  return (
    <Box className={cn('flex flex-col w-full', className)}>
      {label && (
        <Text
          size='sm'
          className='text-sm text-left font-medium whitespace-nowrap font-body leading-4 mb-2'
          fw={500}
        >
          {label}
        </Text>
      )}

      <Box
        className={cn(
          'relative rounded-md border transition-colors',
          isFocused ? 'border-black' : 'border-gray-300',
        )}
      >
        <ActionIcon
          variant='light'
          size='sm'
          className='absolute top-2 right-2 z-[10]'
          onClick={() => {
            const editor = quillRef.current?.getEditor()
            editor?.focus()
            setShowEmojiPicker(prev => !prev)
          }}
        >
          <Smile size={18} color='black' />
        </ActionIcon>

        {showEmojiPicker && (
          <Box className='absolute right-0 top-10 z-50'>
            <Picker
              data={data}
              position='top'
              onEmojiSelect={handleEmojiSelect}
              locale={language.split('-')[0]}
            />
          </Box>
        )}

        <ReactQuill
          ref={quillRef}
          value={field.value}
          onChange={val => helpers.setValue(val)}
          theme='snow'
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, 4, 5, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
            ],
          }}
          formats={['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link']}
        />
      </Box>

      {meta.touched && meta.error && (
        <Text size='xs' color='red' mt='xs'>
          {meta.error}
        </Text>
      )}
    </Box>
  )
}
